/**
 * HUD en jeu, superposé à la 3D, façon jeu de gestion (Cities: Skylines) :
 * barre d'état en bas (horloge, station, ressources, météo) et barre d'outils
 * posée dessus (vues, rubriques de construction, gestion), alertes en haut à
 * gauche, fiche de sélection ou affluence en direct en haut à droite,
 * épingles d'affluence au-dessus des installations et fenêtre de direction
 * (fréquentation, finances, journal).
 *
 * Raccourcis : les lettres suivent le caractère tapé (AZERTY comme QWERTY),
 * les chiffres la touche physique. N : damage de nuit ; Espace, − et +, 1 à 6 :
 * vitesse (pause, plus lent, plus vite, ×1 à ×32).
 */
import { useEffect } from 'preact/hooks';
import { AlertsPanel, Banner, Toasts } from './panels';
import { BuildMenu, PerfPanel } from './tools';
import { TopBar } from './topbar';
import { Dock } from './dock';
import { Inspector } from './inspector';
import { Office } from './office';
import { Pins } from './pins';
import { PistesPanel } from './pistes';
import { TrailMap } from './trailmap/TrailMap';
import { speedHotkey, togglePause } from './speed';
import { hud } from './context';
import { ui, type OfficeTab } from './ui-state';

function toggleOffice(tab: OfficeTab): void {
  if (!hud.store?.isOwner) return;
  ui.office.value = ui.office.value === tab ? null : tab;
}

export function GameHud() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'INPUT' || e.ctrlKey || e.metaKey || e.altKey) return;
      const store = hud.store;
      const host = !!store?.replica && store.replica.world.hostId === store.welcome?.playerId;
      // Échap ferme d'abord la fenêtre de direction, sans toucher à la sélection.
      if (e.code === 'Escape' && ui.office.value) {
        ui.office.value = null;
        e.stopImmediatePropagation();
        return;
      }
      if (e.code === 'Escape' && ui.grooming.value) {
        ui.grooming.value = false;
        e.stopImmediatePropagation();
        return;
      }
      // Puis le plan des pistes, et le panneau de gestion des pistes.
      if (e.code === 'Escape' && (ui.trailMap.value || (ui.pistesPanel.value && !ui.build.value && !ui.pisteEdit.value))) {
        if (ui.trailMap.value) ui.trailMap.value = false;
        else ui.pistesPanel.value = false;
        e.stopImmediatePropagation();
        return;
      }
      // Plan des pistes ouvert : seules L et Échap le concernent.
      if (ui.trailMap.value && e.key.toLowerCase() !== 'l') return;
      switch (e.key.toLowerCase()) {
        case 'm':
          hud.engine?.cam.overview();
          return;
        case 'l':
          ui.trailMap.value = !ui.trailMap.value;
          return;
        case 'k':
          ui.overlay.value = !ui.overlay.value;
          return;
        case 'p':
          ui.pistesPanel.value = !ui.pistesPanel.value;
          return;
        case 'v':
          ui.freeCam.value = !ui.freeCam.value;
          return;
        case 'i':
          ui.pins.value = !ui.pins.value;
          return;
        case 'b':
          if (store?.isOwner) {
            ui.buildMenu.value = !ui.buildMenu.value;
            if (!ui.buildMenu.value) ui.build.value = null;
          }
          return;
        case 'g':
          toggleOffice('finances');
          return;
        case 'f':
          toggleOffice('frequentation');
          return;
        case 'j':
          toggleOffice('journal');
          return;
        case 'n':
          ui.grooming.value = !ui.grooming.value;
          if (ui.grooming.value) ui.selection.value = null;
          return;
      }
      switch (e.code) {
        case 'F3':
          e.preventDefault();
          ui.perf.value = !ui.perf.value;
          break;
        case 'Space':
          if (host && store) {
            e.preventDefault();
            togglePause();
          }
          break;
        default:
          // 1 à 6 : ×1 à ×32 ; − et + : plus lent, plus vite (touches physiques).
          if (host && speedHotkey(e.code)) e.preventDefault();
          break;
      }
    };
    // Phase de capture : Échap est traité avant le mode construction et la sélection.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
  return (
    <>
      <div class="kh-protect-top" />
      <div class="kh-protect-bottom" />
      <Pins />
      <div class="kh-layer">
        <TopBar />
        <Banner />
        <Toasts />
        <PerfPanel />
        <AlertsPanel />
        <Inspector />
        <BuildMenu />
        <PistesPanel />
        <Dock />
        <Office />
      </div>
      <TrailMap />
    </>
  );
}
